import React from 'react';

/**
 * BlogPost - Main container component for blog posts
 * Enforces max-width, padding, and proper spacing
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Blog content
 * @param {string} [props.className] - Additional classes (limited)
 */
export const BlogPost = ({ children, className = '' }) => {
  return (
    <article 
      className={`max-w-3xl mx-auto px-4 md:px-6 py-12 ${className}`}
      data-testid="blog-post"
    >
      {children}
    </article>
  );
};

/**
 * BlogHeader - Header section with title, meta, and optional featured image
 */
export const BlogHeader = ({ 
  title, 
  category, 
  author, 
  publishedAt, 
  readTime,
  featuredImage,
  featuredImageAlt,
  featuredImageCaption 
}) => {
  const formattedDate = publishedAt 
    ? new Date(publishedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  return (
    <header className="mb-8" data-testid="blog-header">
      {category && (
        <span 
          className="inline-block bg-[#1A1A1A] text-[#00E87A] text-sm px-3 py-1 rounded-full mb-4"
          data-testid="blog-category"
        >
          {category}
        </span>
      )}
      
      <h1 
        className="text-4xl md:text-5xl font-bold leading-tight mb-6 text-white"
        data-testid="blog-title"
      >
        {title}
      </h1>
      
      <div className="flex flex-wrap items-center text-gray-400 text-sm mb-8 gap-2 md:gap-4">
        {author && <span data-testid="blog-author">By {author}</span>}
        {author && formattedDate && <span className="hidden md:inline">•</span>}
        {formattedDate && (
          <time dateTime={publishedAt} data-testid="blog-date">
            {formattedDate}
          </time>
        )}
        {readTime && (
          <>
            <span className="hidden md:inline">•</span>
            <span data-testid="blog-read-time">{readTime} min read</span>
          </>
        )}
      </div>

      {featuredImage && (
        <figure className="mb-10" data-testid="blog-featured-image">
          <img 
            src={featuredImage} 
            alt={featuredImageAlt || title}
            className="w-full rounded-xl aspect-video object-cover"
            loading="eager"
          />
          {featuredImageCaption && (
            <figcaption className="mt-3 text-sm text-gray-400 text-center">
              {featuredImageCaption}
            </figcaption>
          )}
        </figure>
      )}
    </header>
  );
};

/**
 * BlogSection - Section wrapper with proper heading spacing
 */
export const BlogSection = ({ children, className = '' }) => {
  return (
    <section className={className} data-testid="blog-section">
      {children}
    </section>
  );
};

/**
 * BlogHeading2 - H2 with enforced spacing
 */
export const BlogHeading2 = ({ children, id }) => {
  return (
    <h2 
      id={id}
      className="text-2xl md:text-3xl font-semibold leading-snug mt-12 mb-4 text-white"
      data-testid="blog-h2"
    >
      {children}
    </h2>
  );
};

/**
 * BlogHeading3 - H3 with enforced spacing
 */
export const BlogHeading3 = ({ children, id }) => {
  return (
    <h3 
      id={id}
      className="text-xl md:text-2xl font-semibold leading-normal mt-8 mb-3 text-white"
      data-testid="blog-h3"
    >
      {children}
    </h3>
  );
};

/**
 * BlogParagraph - Paragraph with enforced spacing and typography
 */
export const BlogParagraph = ({ children }) => {
  return (
    <p 
      className="text-base text-gray-200 leading-relaxed mb-6 last:mb-0"
      data-testid="blog-paragraph"
    >
      {children}
    </p>
  );
};

/**
 * BlogIntro - Introduction paragraph with special styling
 */
export const BlogIntro = ({ children }) => {
  return (
    <p 
      className="text-xl text-gray-200 leading-relaxed mb-6 border-l-4 border-[#00E87A] pl-6 italic"
      data-testid="blog-intro"
    >
      {children}
    </p>
  );
};

/**
 * BlogList - Unordered list with proper styling
 */
export const BlogList = ({ children, ordered = false }) => {
  const Tag = ordered ? 'ol' : 'ul';
  const listStyle = ordered ? 'list-decimal' : 'list-disc';
  
  return (
    <Tag 
      className={`${listStyle} pl-6 space-y-2 text-gray-200 leading-relaxed mb-6`}
      data-testid={ordered ? 'blog-ol' : 'blog-ul'}
    >
      {children}
    </Tag>
  );
};

/**
 * BlogListItem - List item component
 */
export const BlogListItem = ({ children }) => {
  return <li data-testid="blog-list-item">{children}</li>;
};

/**
 * BlogConclusion - Conclusion section with separator
 */
export const BlogConclusion = ({ children }) => {
  return (
    <section 
      className="mt-12 pt-8 border-t border-gray-700"
      data-testid="blog-conclusion"
    >
      {children}
    </section>
  );
};

/**
 * BlogCTA - Call-to-action box
 */
export const BlogCTA = ({ title, description, buttonText, buttonHref }) => {
  return (
    <div 
      className="bg-[#1A1A1A] rounded-lg p-6 mt-8"
      data-testid="blog-cta"
    >
      <h3 className="text-xl font-semibold text-white mb-3">
        {title}
      </h3>
      <p className="text-gray-200 mb-4">
        {description}
      </p>
      <a 
        href={buttonHref}
        className="inline-block bg-[#00E87A] text-[#0D1117] font-semibold px-6 py-3 rounded-lg hover:bg-[#00FF88] transition-colors"
        data-testid="blog-cta-button"
      >
        {buttonText}
      </a>
    </div>
  );
};

export default BlogPost;
